<script setup>
import { ref, computed, watchEffect } from 'vue'
import NavHeader from './NavHeader.vue'
import { useRouter } from 'vue-router'
import { useWebAppStore } from '@/stores/webApp'
import { useI18n } from 'vue-i18n'
import { useWindowSize } from '@vueuse/core'
import { AdsIcon } from '@accor/ads-components'
import { IconChevronDown, IconChevronUp } from '@accor/icons'

const router = useRouter()
const webAppStore = useWebAppStore()
const { height } = useWindowSize()
const { t } = useI18n()
const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: { type: Array, required: true }, //[firt level name, second level id]
  items: { type: Array, required: true } //  [{ name: Eq, label: String, icon: Component }]
})

const subLevel = ref(null)
const subItemsHidden = ref(true)
const firstInteractionNavItems = ref(false)

const emitRouteChange = (...args) => {
  emit('update:modelValue', args)
}

const TOP_HEADER_HEIGHT = 83
const sideNavMaxHeight = computed(() => height.value - TOP_HEADER_HEIGHT + 'px')
const previousFirstLevelId = computed(() => props.modelValue[0])

const toggleCurrentSubItems = (name, subItems) => {
  firstInteractionNavItems.value = true
  if (subItems?.length && previousFirstLevelId.value === name) {
    subItemsHidden.value = !subItemsHidden.value
  } else if (subItems?.length && previousFirstLevelId.value !== name) {
    subItemsHidden.value = false
  } else {
    subItemsHidden.value = true
  }
}

const isChevronDownDisplayed = (name, subItems) =>
  subItems?.length && (previousFirstLevelId.value !== name || subItemsHidden.value)

const isChevronUpDisplayed = (name, subItems) =>
  subItems?.length && (previousFirstLevelId.value === name || !subItemsHidden.value)

watchEffect(() => {
  if (!subItemsHidden.value && subLevel?.value?.[0] && firstInteractionNavItems.value) {
    subLevel?.value[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
})
</script>

<template>
  <aside class="side-nav">
    <template v-if="!webAppStore.isUserInfoReady">
      <div class="side-nav__placeholder"></div>
      <div class="side-nav__placeholder side-nav__placeholder--square"></div>
      <div v-for="el in 4" class="side-nav__placeholder" :key="el"></div>
    </template>
    <template v-else>
      <NavHeader class="side-nav__header" />
      <hr class="side-nav__separator" />
      <nav>
        <ul class="side-nav__list">
          <li
            v-for="{ name, label, subItems, icon, className, redirectUrl } in items"
            :key="name"
            class="side-nav__list-item"
            :class="{
              [className]: className,
              'side-nav__list-item--selected': previousFirstLevelId === name
            }"
          >
            <a
              class="side-nav__link-item"
              :href="redirectUrl ?? router.resolve({ name, locale: $i18n.locale }).href"
              @click.stop.prevent="(emitRouteChange(name), toggleCurrentSubItems(name, subItems))"
            >
              <div class="side-nav__first-level">
                <span class="side-nav__list-label">
                  <AdsIcon v-if="icon" :icon="icon" size="sm" />
                  {{ t(label) }}
                </span>
                <AdsIcon
                  v-if="isChevronDownDisplayed(name, subItems)"
                  :icon="IconChevronDown"
                  size="sm"
                />
                <AdsIcon
                  v-else-if="isChevronUpDisplayed(name, subItems)"
                  :icon="IconChevronUp"
                  size="sm"
                />
              </div>
            </a>

            <div
              ref="subLevel"
              v-if="subItems?.length && previousFirstLevelId === name && !subItemsHidden"
              class="side-nav__sub-level"
            >
              <div class="side-nav__vl"></div>
              <ul>
                <li
                  v-for="subItem in subItems"
                  :key="subItem.name"
                  class="side-nav__list-item"
                  :class="{
                    [subItem.className]: subItem.className,
                    'side-nav__list-item--selected-white': props.modelValue[1] === subItem.name
                  }"
                  @click.stop.prevent="emitRouteChange(name, subItem.name)"
                >
                  <a
                    class="side-nav__link-item"
                    :href="router.resolve({ name: subItem.name }).href"
                  >
                    {{ t(subItem.label) }}
                  </a>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    </template>
  </aside>
</template>

<style lang="scss">
$root: '.side-nav';

#{$root} {
  padding: ccf.$spacing-inner-05 ccf.$spacing-inner-04;
  display: flex;
  flex-direction: column;
  gap: ccf.$spacing-outer-01;
  max-height: v-bind('sideNavMaxHeight');

  &__separator {
    border-top: 1px solid ccf.$color-stroke;
    margin: ccf.$spacing-outer-01 0 ccf.$spacing-outer-02;
  }

  &__list-item {
    @include ccf.ui-body-01;
    cursor: pointer;
    border-radius: ccf.$border-radius;
    border: 1px solid transparent;

    &--selected {
      @extend #{$root}__list-item;
      background-color: ccf.$background-loyalty-default;
      font-weight: 500;
      margin-top: 1px;
    }

    &--selected-white {
      @extend #{$root}__list-item;
      background-color: white;
      font-weight: 500;
    }

    &:hover {
      border: 1px solid ccf.$color-outline;
    }
  }

  &__link-item {
    display: flex;
    padding: calc(ccf.$spacing-inner-04 - 1px);
  }

  &__first-level {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: ccf.$spacing-inner-04;
    justify-content: space-between;
    width: 100%;
  }

  &__sub-level {
    padding: ccf.$spacing-inner-04;
    display: flex;
    flex-direction: row;
  }

  &__vl {
    margin-right: ccf.$spacing-inner-04;
    border-left: 1px solid black;
  }

  &__placeholder {
    width: 100%;
    background: linear-gradient(75deg, #dadadd 15%, #c0bfc4 25%, #dadadd 45%);
    animation: placeHolderShimmer 4.5s linear infinite forwards;
    height: ccf.$spacing-inner-05;
    margin-bottom: ccf.$spacing-inner-05;
    border-radius: ccf.$border-radius;

    &--square {
      height: 60px;
      margin-bottom: ccf.$spacing-inner-07;
    }
  }

  &__list-label {
    display: flex;
    align-items: center;
    gap: ccf.$spacing-inner-01;
  }

  &__unlock-advantage {
    color: #5f5bd2;

    .ads-icon {
      fill: #5f5bd2;
      transition: transform 0.1s ease;
      transform-origin: center;
    }

    &:hover {
      color: #3a34ab;

      .ads-icon {
        fill: #3a34ab;
        transform: rotate(-10deg);
      }
    }
  }
}
</style>
